import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { suppliers, type Input, type Supplier } from "@/app/master";
import { loadingOf, useList } from "@/app/queries";
import { Alert, Button, EmptyState, Input as TextInput, PageHeader, Pill, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { SupplierForm } from "./SupplierForm";
import { supplierDraft, supplierInput } from "./supplierDraft";

/** 供应商列表：新建与编辑（含停用）在抽屉里。系统供应商（决策 D203）只读，不显示编辑。 */
export function Suppliers() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: Supplier | null } | null>(null);
  const list = useList("suppliers", suppliers);
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? []).filter((s) => !keyword || s.name.toLowerCase().includes(keyword));

  return (
    <>
      <PageHeader title="供应商" description="上游供应商。结算币种以各通道成本价上的币种为准（决策 D85）。" actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建供应商</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{shown.length} 条</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索供应商名称" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th w="id">ID</Th><Th w="name">名称</Th><Th w="status">状态</Th><Th w="wide">备注</Th><Th /></tr></thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.id}>
                  <td className="ax-mono">{s.id}</td><td>{s.name} {s.system && <Pill plain>系统预置</Pill>}</td><td><EntityStatusPill status={s.status} /></td><td className="ax-secondary">{s.note}</td>
                  <td>{!s.system && <Button variant="ghost" size="sm" onClick={() => setEditing({ row: s })}>{canWrite ? "编辑" : "查看"}</Button>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有供应商">先新建供应商，再在通道配置页为它添加通道。</EmptyState>
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑供应商 ${editing.row.name}` : "新建供应商"} initial={supplierDraft(editing.row ?? undefined)} convert={supplierInput}
          save={(body: Input<Supplier>) => (editing.row ? suppliers.update(editing.row.id, body) : suppliers.create(body))}
          invalidate={["suppliers"]} message={editing.row ? "已更新供应商" : "已新建供应商"} deletion={editing.row ? deleteTarget("供应商", editing.row.name, suppliers, editing.row.id, ["suppliers"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <SupplierForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} />}
        </EntityDrawer>
      )}
    </>
  );
}
